import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport';
import { BARCODE_FIX_MAX_ITEMS } from '@/api/handlers/resources/products/requests';
import { getNodeTestIdProp } from '@/utils';
import type { BarcodeIssuesSubmitMode } from './barcodeIssuesMeta';
import type { BarcodeIssuesBarState } from './useBarcodeIssues';

const COPY = 'products.barcodeIssues.bar';

type BarcodeIssuesBarProps = {
    bar: BarcodeIssuesBarState;
    onSubmit: (mode: BarcodeIssuesSubmitMode) => void;
};

/**
 * The sticky bar while rows are selected: what is selected and how many
 * barcodes that generates, **Keep N variants as is**, **Apply suggestions to
 * N** and **Apply fixes to N**. It stays outside the table's scroll, at the
 * bottom of the viewport.
 */
export const BarcodeIssuesBar: React.FC<BarcodeIssuesBarProps> = ({
    bar,
    onSubmit,
}) => {
    const { t } = useTranslation('pages');
    const isBlocked = bar.isTooMany || bar.isApplying;

    return (
        <Card
            className="sticky bottom-4 z-10 flex-row flex-wrap items-center gap-3 p-3"
            {...getNodeTestIdProp('barcode-issues-bar')}
        >
            <div className="flex min-w-0 flex-1 flex-col">
                <Typography type="body-sm">
                    {t(`${COPY}.selected`, {
                        count: bar.selectedCount,
                        selected: countParam(bar.selectedCount),
                    })}
                </Typography>
                <Typography color="muted" type="body-xs">
                    {bar.isTooMany
                        ? t(`${COPY}.tooMany`, {
                              max: countParam(BARCODE_FIX_MAX_ITEMS),
                          })
                        : bar.toGenerateCount > 0
                          ? t(`${COPY}.toGenerate`, {
                                count: bar.toGenerateCount,
                                barcodes: countParam(bar.toGenerateCount),
                            })
                          : null}
                </Typography>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button
                    isDisabled={isBlocked}
                    onPress={() => onSubmit('keep')}
                    variant="secondary"
                    {...getNodeTestIdProp('barcode-issues-keep')}
                >
                    {t(`${COPY}.keep`, {
                        count: bar.variantCount,
                        selected: countParam(bar.variantCount),
                    })}
                </Button>
                <Button
                    isDisabled={isBlocked}
                    onPress={() => onSubmit('suggestions')}
                    variant="secondary"
                    {...getNodeTestIdProp('barcode-issues-apply-suggestions')}
                >
                    {t(`${COPY}.applySuggestions`, {
                        count: bar.selectedCount,
                        selected: countParam(bar.selectedCount),
                    })}
                </Button>
                <Button
                    isDisabled={isBlocked || !bar.canApplyChosen}
                    isPending={bar.isApplying}
                    onPress={() => onSubmit('chosen')}
                    {...getNodeTestIdProp('barcode-issues-apply')}
                >
                    {bar.isApplying
                        ? t(`${COPY}.applying`)
                        : t(`${COPY}.apply`, {
                              count: bar.selectedCount,
                              selected: countParam(bar.selectedCount),
                          })}
                </Button>
            </div>
        </Card>
    );
};
